/**
 * When enveloping a third party app form, manages the customization and functionality of it.
 * @property {string} dataset.formType - [data-form-type] will set the type of the form.
 * @property {string} dataset.formSelector - [data-form-selector] will set the selector of the third party form.
 * @property {string} dataset.cssAddress - [data-css-address] will set the css file to be fetched to style the shadow DOM.
 * @example <form-manager data-form-type="type" data-form-selector="#selector" data-css="./form.css">[formContent]</form-manager>
 */
class FormManager extends HTMLElement {
  submissionDisabled = false;

  constructor() {
    super();
    this.formSelector = this.dataset.formSelector || '';
    this.formType = this.dataset.formType || '';
    this.cssAddress = this.dataset.cssAddress || '';
    this.init();
  }

  /**
   * Initializes the form manager by setting up the form and applying customizations.
   * @private
   */
  async init() {
    this.signupForm = await this.catchSignupForm();
    if (this.signupForm) {
      this.initElements();
      this.applyStyles();
      this.initSignupForm();
    }
  }

  /**
   * Observes the DOM to detect when the signup form is available.
   * @returns {Promise<HTMLElement>} The detected signup form element.
   * @private
   */
  catchSignupForm() {
    return new Promise((resolve) => {
      const form = this.querySelector(this.formSelector);
      if (form) return resolve(form);

      const observer = new MutationObserver((e) => {
        const detectedForm = this.querySelector(this.formSelector);
        if (detectedForm) {
          resolve(detectedForm);
          observer.disconnect();
        }
      });

      observer.observe(this, { subtree: true, childList: true });
    });
  }

  /**
   * Initializes form elements by selecting them from the DOM.
   * @private
   */
  initElements() {
    this.rootEmbed = this.signupForm.shadowRoot.querySelector('#app-embed');
    this.form = this.signupForm.shadowRoot.querySelector('form');
    this.formInputs = {}
    this.form.querySelectorAll('input, textarea').forEach(element => {
      this.formInputs[element.id.replace('custom#','')] = {
        element,
        ...(element.closest('fieldset')?.querySelector('legend') && {
          legend: element.closest('fieldset').querySelector('legend')
        })
      }
    });
    this.formEmailCheckbox = this.form.querySelector('fieldset[name="custom#email_signup_checkbox"]');
    this.formSubmit = this.form.querySelector('button[type="submit"]');
  }

  /**
   * Applies custom styles to the signup form.
   * @private
   */
  async applyStyles() {
    const styleContent = await this.generateStyleContent();
    const style = document.createElement('style');
    style.innerHTML = styleContent;
    this.signupForm.shadowRoot.appendChild(style);

    const headerElem = this.rootEmbed.querySelector('h2');
    headerElem.className = '';
    if (this.formType == 'footer') headerElem.classList.add('footer-block__heading');
    else headerElem.classList.add('form-block__heading');

    /* Disable the form submit by default. */
    this.formSubmit.disabled = true;

    this.signupForm.shadowRoot.getElementById('label-email').innerHTML = 'Email Address*';

    /* Move the disclaimer above the form submit button */
    this.formSubmit.before(this.rootEmbed.querySelector('div[class^="_formDisclaimer"]'));
  }

  /**
   * Generates custom CSS to be injected into the form's shadow DOM.
   * @returns {string} The generated CSS content.
   * @private
   */
  async generateStyleContent() {
    let styleContent = '';

    try {
      const response = await fetch(this.cssAddress);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const cssContent = await response.text();
      styleContent = cssContent;

    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }

    return styleContent;    
  }

  /**
   * Initializes the signup form, attaching event listeners and setting up validation.
   * @private
   */
  initSignupForm() {
    this.handleFormValidationOnChange();
    this.handleFormSubmit();
  }

  /**
   * Helper function that verifies if an email is invalid, checking if it is empty or it doesn't match valid format.
   * @private
   */
  isInvalidEmail = (email) => {
    const isEmpty = email.trim() === '';
    const regex =
      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    const isValid = regex.test(email);
    return isEmpty || !isValid;
  };


  /**
   * Handles form validation on change events
   *
   * - Adds an event listener to the form that triggers whenever a change is detected.
   * - Validates the form fields and determines whether the submit button should be enabled or disabled.
   *
   * Validation checks include:
   * - The email address is valid.
   * 
   * If any of these validations fail, the submit button is disabled.
   */
  handleFormValidationOnChange() {
    /* Add event listener to detect changes to the form and toggle the
     * state of the submit button when required field values are present. */
    this.form.addEventListener('change', () => {
      const validations = [
        this.isInvalidEmail(this.formInputs.email.element.value),
        this.submissionDisabled,
      ];

      this.formSubmit.disabled = validations.some(Boolean);
    });
  }

  /**
   * Handles the form submission event.
   *
   * - Listens for the form's submit event and prevents the default behavior to handle submission manually.
   * - Hashes the email address using the SHA-256 algorithm to generate a unique subscriber key.
   * - Constructs a POST request with the hashed email and other subscriber data to send to a specified API endpoint.
   * - The request is sent to register the subscriber's information via the provided API.
   *
   * The method includes:
   * - `digestMessage`: An asynchronous helper function to hash the email address.
   * - Event listener for form submission which:
   *   - Hashes the email address.
   *   - Constructs the subscriber key.
   *   - Sends the data to an external API endpoint using the `fetch` method.
   *   - Handles API response and errors.
   */
  handleFormSubmit() {
    const digestMessage = async (message) => {
      const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8); // hash the message
      const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
      return hashHex;
    };

    const performJourneyEnrollment = async () => {
      const emailAddress = this.formInputs.email.element.value.trim().toLowerCase();
      let subscriberKey = 'ROG_';
      let emailAddressHash = digestMessage(emailAddress);
      subscriberKey = subscriberKey + emailAddressHash;
      subscriberKey = subscriberKey.toUpperCase();

      const headers = new Headers();
      headers.append('Content-Type', 'application/json');

      const body = JSON.stringify({
        ContactKey: subscriberKey,
        EventDefinitionKey: 'APIEvent-01ca800e-1e82-fbd1-1df6-4c2541970fef',
        Data: {
          SubscriberKey: subscriberKey,
          EmailAddress: emailAddress,
          FIRST_NAME: 'N/A',
          GENDER: 'M',
          OPT_STATUS: true,
        },
      });

      const request = new Request('https://awsna.api.kenvue.com/rog/v1/events', {
        method: 'POST',
        headers: headers,
        body: body,
        redirect: 'follow',
        mode: 'cors',
      });

      await fetch(request)
        .then((response) => response.text())
        .then((result) => console.log(result))
        .catch((error) => console.error(error));
    };

    /* Add event listener to handle form submit. */
    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      console.log('Running the form submit listener.');

      if (this.formEmailCheckbox && !this.formEmailCheckbox.querySelector('input')?.checked) return;
      await performJourneyEnrollment();
    });
  }
}

customElements.define('form-manager', FormManager);

/**
 * Manages the customization and functionality of a third party app form for Signup.
 * @property {string} dataset.formType - [data-form-type] will set the type of the form.
 * @property {string} dataset.formSelector - [data-form-selector] will set the selector of the third party form.
 * @property {string} dataset.cssAddress - [data-css-address] will set the css file to be fetched to style the shadow DOM.
 * @example <form-manager-signup data-form-type="type" data-form-selector="#selector" data-css="./form.css">[formContent]</form-manager-signup>
 */
class FormManagerSignup extends FormManager {
  fieldsetsToHide = [
    'custom#consumer_health_data_collection_consent',
    'custom#consumer_health_data_sharing_consent',
    'custom#consumer_health_data_media_consent'
  ]

  constructor() {
    super()
  }

  initSignupForm() {
    this.handleNvwaResidencyChange();
    super.initSignupForm();
  }

  initElements() {
    super.initElements();
    this.fieldsetsToHide = this.fieldsetsToHide.map(fieldset => this.form.querySelector(`fieldset[name="${fieldset}"]`));
  }

  applyStyles() {
    super.applyStyles();

    const hideElement = (elem) => elem.classList.add('hidden');

    const legendsToHide = [
      'email_signup_checkbox', 
      'nvwa_residency-true',
      'consumer_health_data_collection_consent-true',
      'consumer_health_data_sharing_consent-true',
      'consumer_health_data_media_consent-true'
    ]

    /* Hide the legend elements */
    legendsToHide.forEach(legend => {
      if (this.formInputs[legend]) hideElement(this.formInputs[legend].legend)
    });
    /* Hide the Consumer Healthcare Data Consent input elements */
    this.fieldsetsToHide.forEach(fieldset => {
      if (fieldset) hideElement(fieldset)
    });

    /* Add links in the consent notices */
    this.fieldsetsToHide.forEach(
      (elem) => {
        const spanElem = elem.querySelector('span');
        spanElem.innerHTML = spanElem.innerHTML.replace(
          /Privacy Policy/g,
          '<a href="https://www.kenvue.com/privacy-policy/us" class="external-link" target="_blank">Privacy Policy</a>'
        );
        spanElem.innerHTML = spanElem.innerHTML.replace(
          /CHD Privacy Notice/g,
          '<a href="https://www.kenvue.com/privacy-policy/us#consumer-health-data" class="external-link" target="_blank">CHD Privacy Notice</a>'
        );
        spanElem.innerHTML = spanElem.innerHTML.replace(
          /clicking here\./,
          'clicking <a href="https://privacyportal.onetrust.com/webform/82718966-5f5b-4890-b678-92f1798255c7/7d2d6d0e-787c-40c2-b035-7d40a43f57a7?digitalassetname=rogaine.com" class="external-link" target="_blank">here</a>.'
        );
      }
    );

    /* Update policy links in the disclaimer */
    this.rootEmbed.querySelectorAll('div[class^="_formDisclaimer"] a').forEach((elem) => {
      elem.classList.add('external-link');
    });

    /* Fix product interests radio button group */

    const productInterestsContainer = this.rootEmbed.querySelector(
      'fieldset[name*="product_interests"] > div[class^="_horizontalLayout"]'
    );

    if (productInterestsContainer) {
      productInterestsContainer.style.gridTemplateColumns = '1fr 1fr';
    }
  }

  /**
   * Handles form validation on change events
   *
   * - Adds an event listener to the form that triggers whenever a change is detected.
   * - Validates the form fields and determines whether the submit button should be enabled or disabled.
   *
   * Validation checks include:
   * - The email address is valid.
   * - If the NVWA Residency input is checked, then both Consumer Healthcare Data Consent inputs must be checked.
   * 
   * If any of these validations fail, the submit button is disabled.
   */
  handleFormValidationOnChange() {
    /* Add event listener to detect changes to the email signup form and toggle the
     * state of the submit button when required field values are present. */
    this.form.addEventListener('change', () => {
      const isNvwaConsentIncomplete = () =>
        this.formInputs['nvwa_residency-true'].element.checked &&
        (!this.formInputs['consumer_health_data_sharing_consent-true'].element.checked || !this.formInputs['consumer_health_data_collection_consent-true'].element.checked);

      const validations = [
        this.isInvalidEmail(this.formInputs.email.element.value),
        isNvwaConsentIncomplete(),
        this.submissionDisabled,
      ];
 
      this.formSubmit.disabled = validations.some(Boolean);
    });
  }

  /**
   * Handles changes to the NVWA Residency input, toggling the visibility and requirements
   * of the Consumer Healthcare Data Consent input elements based on the state of the checkbox.
   *
   * - When the checkbox is checked:
   *   - The form validation is re-enabled.
   *   - The Consumer Healthcare Data Consent fields are shown and set as required.
   *   - The submit button is disabled until all required fields are completed.
   *
   * - When the checkbox is unchecked:
   *   - The form validation is disabled.
   *   - The Consumer Healthcare Data Consent fields are hidden and no longer required.
   *   - The submit button is enabled.
   */
  handleNvwaResidencyChange() {
    this.formInputs['nvwa_residency-true'].element.addEventListener('change', (event) => {
      const isChecked = event.currentTarget.checked
      this.form.toggleAttribute('novalidate', !isChecked);
      this.fieldsetsToHide.forEach(fieldset => fieldset.classList.toggle('hidden', !isChecked))
      this.fieldsetsToHide.forEach(fieldset => {
        if (fieldset.name != "custom#consumer_health_data_media_consent") fieldset.querySelector('input').toggleAttribute('required', isChecked)
      })
      this.formSubmit.toggleAttribute('disabled', isChecked)
    });
  }
}

customElements.define('form-manager-signup', FormManagerSignup);

/**
 * Manages the customization and functionality of a third party app form for the Hatsoff Quiz.
 * @property {string} dataset.formType - [data-form-type] will set the type of the form.
 * @property {string} dataset.formSelector - [data-form-selector] will set the selector of the third party form.
 * @property {string} dataset.cssAddress - [data-css-address] will set the css file to be fetched to style the shadow DOM.
 * @example <form-manager-hatsoff data-form-type="type" data-form-selector="#selector" data-css="./form.css">[formContent]</form-manager-hatsoff>
 */
class FormManagerHatsoff extends FormManagerSignup {
  constructor() {
    super();
  }

  initElements() {
    super.initElements();
    if (this.formInputs.quiz_result_data) this.formInputs.quiz_result_data.element.parentElement.style.display = 'none';
    this.formMonthsDropdown = this.form.querySelector('button[aria-controls="custom#month-selector-dropdown"]')?.parentElement;
    this.formYearsDropdown = this.form.querySelector('button[aria-controls="custom#year-selector-dropdown"]')?.parentElement;
  }

  applyStyles() {
    super.applyStyles();

    if (this.formMonthsDropdown && this.formYearsDropdown) {
      const birthdayContainer = document.createElement('div');
      birthdayContainer.classList.add('birthday-container');

      const birthdayLabel = document.createElement('span');
      birthdayLabel.classList.add('birthday-label');

      const b = document.createElement('b');
      b.textContent = 'Enter your birthday';
      birthdayLabel.appendChild(b);
      birthdayLabel.append(' (Optional)');

      birthdayContainer.appendChild(birthdayLabel);

      const birthdayWrapper = document.createElement('div');
      birthdayWrapper.classList.add('birthday-wrapper');

      this.formMonthsDropdown.parentElement.insertBefore(birthdayContainer, this.formMonthsDropdown);

      birthdayWrapper.appendChild(this.formMonthsDropdown);
      birthdayWrapper.appendChild(this.formYearsDropdown);
      birthdayContainer.appendChild(birthdayWrapper);

      const monthsLabelSpan = this.formMonthsDropdown.querySelector('button span');
      monthsLabelSpan.classList.add('birthday-dropdown-months-label');

      const yearsLabelSpan = this.formYearsDropdown.querySelector('button span');
      yearsLabelSpan.classList.add('birthday-dropdown-years-label');
    }

    if (this.formInputs.first_name && this.formInputs.last_name) {
      const nameWrapper = document.createElement('div');
      nameWrapper.classList.add('name-wrapper');

      const firstNameDiv = this.formInputs.first_name.element.parentElement;
      const lastNameDiv = this.formInputs.last_name.element.parentElement;

      firstNameDiv.classList.add('name-input-wrapper');
      lastNameDiv.classList.add('name-input-wrapper');

      firstNameDiv.parentElement.insertBefore(nameWrapper, firstNameDiv);
      nameWrapper.appendChild(firstNameDiv);
      nameWrapper.appendChild(lastNameDiv);
    }
  }

  async generateStyleContent() {
    const previousContent = await super.generateStyleContent();

    let cssToHideYears = '';

    if (this.formYearsDropdown) {
      const years = Array.from({ length: 201 }, (_, index) => 2100 - index);
      const currentYear = new Date().getFullYear();
      const yearsToHide = years.filter((year) => year > currentYear);
      const yearsSelectors = yearsToHide.map((year) => `#select-custom\\#year-${year}`);
      cssToHideYears = `
        ${yearsSelectors.join(', ')} { display:none }
      `;
    }

    return previousContent + cssToHideYears;
  }

  /**
   * Updates the value of the quiz result input field if it exists.
   *
   * @param {string} value - The value to be set in the quiz result input field.
   * @param {boolean} submissionDisabled - Value to determine if the form submission needs to be disabled
   */
  updateQuizResult(value, submissionDisabled) {
    if (this.formInputs.quiz_result_data) {
      this.formInputs.quiz_result_data.element.value = value;
      this.formInputs.quiz_result_data.element.dispatchEvent(new Event('input'));
    }
    if (typeof submissionDisabled == 'boolean') {
      this.submissionDisabled = submissionDisabled;
    }
  }
}

customElements.define('form-manager-hatsoff', FormManagerHatsoff);